import React, {useCallback} from 'react';
import {Box, Button, Modal, Typography, Alert} from '@mui/material';
import {Control, FieldValues, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import styles from '../../styles';
import FormTextField from '../shared/FormTextField';
import {pairDevice} from '../../api/adminAPI';

type PairDeviceForm = {
  profileId: string;
};

interface Props {
  deviceId: number;
  onPaired: () => void;
}

const PairDeviceModal: React.FC<Props> = ({deviceId, onPaired}) => {
  const {t} = useTranslation();
  const [showPairDialog, setShowPairDialog] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const {handleSubmit, reset, control} = useForm<PairDeviceForm>({
    defaultValues: {
      profileId: '',
    },
  });

  const handlePairDialogClick = useCallback(() => {
    setShowPairDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    reset();
    setPending(false);
    setError(null);
    setShowPairDialog(false);
  }, [reset]);

  const handlePairSubmit = useCallback(
    async (data: PairDeviceForm) => {
      setPending(true);
      setError(null);

      try {
        const result = await pairDevice({
          deviceId,
          profileId: parseInt(data.profileId, 10),
        });

        if (result.success) {
          onPaired();
          handleDialogClose();
        } else {
          setError(result.error || t('admin.devices.pairError'));
          setPending(false);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : t('admin.devices.pairError'),
        );
        setPending(false);
      }
    },
    [deviceId, handleDialogClose, onPaired, t],
  );

  return (
    <>
      <Button variant="contained" onClick={handlePairDialogClick}>
        {t('admin.devices.pairDevice')}
      </Button>
      <Modal
        open={showPairDialog}
        onClose={handleDialogClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('admin.devices.pairDevice')}
          </Typography>
          <form onSubmit={handleSubmit(handlePairSubmit)} className="form">
            <FormTextField
              control={control as unknown as Control<FieldValues>}
              name="profileId"
              label={t('admin.devices.profileId') as string}
              numbersOnly
              required
            />
            {error && (
              <Alert severity="error" sx={{mt: 2}}>
                {error}
              </Alert>
            )}
            <Button variant="contained" disabled={pending} type="submit">
              {pending ? t('admin.devices.pairing') : t('general.submit')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(PairDeviceModal);
